import { MarketingOverviewModel } from '../../../types';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import GeneralDialog from '../../shared/general-dialog';
import dayjs from 'dayjs';
import { handleErrors } from '../../utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../state/zustand-store';
import {
  resetAllMarketingModels,
  updateMarketingOverview,
  UserKeys,
} from '../../api/users-api';
import * as analytics from '../../shared/analytics/analytics.json';
import { useAnalytics } from '../../shared/analytics/create-events';
import { ModuleList } from '../../shared/analytics/module-list';
import { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { AlertColor } from '@mui/material/Alert';

interface CreateEditMarketingBarDialogProps {
  open: boolean;
  handleClose: () => void;
}

interface CreateDialogProps extends CreateEditMarketingBarDialogProps {
  dialogType: 'create';
  selectedBar: null;
}

interface EditDialogProps extends CreateEditMarketingBarDialogProps {
  dialogType: 'update';
  selectedBar: MarketingOverviewModel;
}

export default function CreateEditMarketingBarDialog({
  open,
  handleClose,
  selectedBar,
  dialogType,
}: CreateDialogProps | EditDialogProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const [resetModels, setResetModels] = useState(true);

  const barTypes = [
    { value: 'Live', niceName: 'Closeable' },
    { value: 'Permanent', niceName: 'Non-Closable' },
  ];

  const buttonOptions = [
    { value: 'info', niceName: 'Info' },
    { value: 'success', niceName: 'Success' },
    { value: 'warning', niceName: 'Warning' },
    { value: 'error', niceName: 'Error' },
  ];

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<MarketingOverviewModel>({
    defaultValues:
      dialogType === 'create'
        ? {
            dateCreated: dayjs(new Date()).valueOf(),
            url: '',
            title: '',
            barColor: 'info',
            textColor: '',
            buttonColor: '',
            buttonTextColor: '',
            buttonText: '',
            active: true,
            openInNewTab: true,
            barType: 'Live',
            hasButton: false,
          }
        : {
            ...selectedBar,
          },
  });

  const isActive = watch('active');

  useEffect(() => {
    if (isActive) {
      setResetModels(true);
    } else {
      setResetModels(false);
    }
  }, [isActive]);

  const { mutate, isPending } = useMutation({
    mutationFn: (data: MarketingOverviewModel) => {
      return updateMarketingOverview(data);
    },
    onSuccess: (returnData, data) => {
      let successMessage = '';
      switch (dialogType) {
        case 'create':
          successMessage = 'Marketing Bar created successfully!';
          break;
        case 'update':
          successMessage = 'Marketing Bar updated successfully!';
          break;
      }

      setSnackbar({
        open: true,
        severity: 'success',
        message: successMessage,
      });

      const newBar = {
        ...(data as MarketingOverviewModel),
        id: data.id || returnData,
      };

      queryClient.setQueryData(
        [UserKeys.MarketingOverviewModels],
        (oldData: { results: MarketingOverviewModel[] }) => {
          if (!oldData?.results) return oldData;
          return [...oldData.results, newBar];
        }
      );

      queryClient.invalidateQueries({
        queryKey: [UserKeys.MarketingOverviewModels],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserSettings],
      });

      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue with the operation. Please refresh and try again.',
      });
    },
    onSettled: () => {
      const actionKey =
        `${dialogType}_marketing_bar` as keyof typeof analytics.gpanel_view.marketing_manager;
      createEvent(analytics.gpanel_view.marketing_manager[actionKey], {
        module: ModuleList.modules.gpanelView.name,
        section: ModuleList.modules.gpanelView.sections.marketingManager,
      });
      if (resetModels) {
        resetAllMarketingModels();
      }
    },
  });

  const onSubmit: SubmitHandler<MarketingOverviewModel> = (data) => {
    mutate(data);
  };

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      maxWidth="md"
      title={
        dialogType === 'create'
          ? 'Create New Marketing Bar'
          : 'Edit Marketing Bar'
      }
      dialogContent={
        <Stack sx={{ px: 6 }} gap={3}>
          <Stack gap={2}>
            <Stack>
              <Stack flexDirection="row" gap={1}>
                <Controller
                  name="active"
                  control={control}
                  render={({ field: props }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...props}
                          checked={props.value}
                          onChange={(e) => {
                            props.onChange(e.target.checked);
                          }}
                        />
                      }
                      label="Set Active"
                    />
                  )}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={resetModels}
                      onChange={(e) => setResetModels(e.target.checked)}
                      disabled={!isActive}
                    />
                  }
                  label="Reset Marketing Models"
                />
              </Stack>
              {isActive && (
                <Alert severity="warning">
                  Marketing Bars that are set as active will reset marketing
                  models by default. You can prevent this by unchecking the
                  "Reset Marketing Models" checkbox above.
                </Alert>
              )}
            </Stack>

            <Controller
              name={'barType'}
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ width: 300 }}>
                  <InputLabel id="bar-type-label">Bar Type</InputLabel>
                  <Select
                    {...field}
                    labelId="bar-type-label"
                    id="barType"
                    label="Bar Type"
                  >
                    {barTypes.map((option) => (
                      <MenuItem value={option.value} key={option.value}>
                        {option.niceName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name={'title'}
              control={control}
              rules={{
                required: 'Bar Title is required.',
              }}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'title',
                  }}
                  InputLabelProps={{
                    htmlFor: 'title',
                  }}
                  label="Bar Title"
                  fullWidth
                  autoComplete="off"
                  error={!!errors.title}
                  helperText={errors?.title?.message || ' '}
                  required
                  {...field}
                />
              )}
            />
            <Controller
              name={'barColor'}
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ width: 300 }}>
                  <InputLabel id="demo-simple-select-label">
                    Bar Color
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Bar Color"
                  >
                    {buttonOptions.map((option) => (
                      <MenuItem value={option.value} key={option.value}>
                        {option.niceName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
          <Stack>
            <Controller
              name="hasButton"
              control={control}
              render={({ field: props }) => (
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => {
                          props.onChange(e.target.checked);
                          if (!e.target.checked) {
                            setValue('buttonText', '');
                            setValue('url', '');
                          }
                        }}
                      />
                    }
                    label="Include Button"
                  />
                </span>
              )}
            />

            <Stack
              flexDirection="row"
              alignItems="baseline"
              width="100%"
              gap={1}
            >
              <Controller
                name={'buttonText'}
                control={control}
                rules={{
                  validate: (value) => {
                    return watch('hasButton')
                      ? value
                        ? true
                        : 'Button Text is required.'
                      : true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'buttonText',
                    }}
                    InputLabelProps={{
                      htmlFor: 'buttonText',
                    }}
                    label="Button Text"
                    fullWidth
                    disabled={!watch('hasButton')}
                    autoComplete="off"
                    error={!!errors.buttonText}
                    helperText={errors?.buttonText?.message || ' '}
                    required
                  />
                )}
              />
              <Controller
                name={'url'}
                control={control}
                rules={{
                  validate: (value) => {
                    if (watch('hasButton')) {
                      if (!value) return 'URL link is required.';
                      const urlPattern =
                        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
                      return (
                        urlPattern.test(value) ||
                        'Enter a valid URL (e.g., https://example.com)'
                      );
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'url',
                    }}
                    InputLabelProps={{
                      htmlFor: 'url',
                    }}
                    label="URL Link"
                    fullWidth
                    disabled={!watch('hasButton')}
                    autoComplete="off"
                    error={!!errors.url}
                    helperText={errors?.url?.message || ' '}
                    required
                  />
                )}
              />
            </Stack>
            <Typography sx={{ fontWeight: 'light', mb: 1 }}>
              {'Preview:'}
            </Typography>
            <Alert
              severity={watch('barColor') as AlertColor}
              variant="filled"
              sx={{
                width: '100%',
                borderRadius: 0,
                alignItems: 'center',
              }}
            >
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                gap={2}
                alignItems="center"
              >
                {watch('title')}
                {watch('hasButton') && (
                  <Button color="inherit" size="small" variant="outlined">
                    {watch('buttonText')}
                  </Button>
                )}
              </Stack>
            </Alert>
          </Stack>
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending,
        title: dialogType === 'create' ? 'Add' : 'Update',
      }}
    />
  );
}
